/**
 * Router-level route availability — the second, non-react-query config path.
 *
 * `beforeLoad` runs before any provider is mounted, so it cannot use
 * `usePaymentConfig`. It does its own raw `fetch` of the same endpoint. Two
 * config paths therefore exist and **both** must read `byokEnabled`; a change
 * applied to only one produces the worst outcome — a router that hides a route
 * the components would have rendered, or vice versa.
 *
 * Lives in its own module (rather than inline in `main.tsx`) so it is testable
 * without booting the app: importing `main.tsx` calls `createRoot`.
 *
 * @module routeAvailability
 */

import { notFound, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";

import { paywallKeys, readBootstrappedConfig, type PaymentConfigPublic } from "@shared/hooks/use-paywall";
import {
  fetchSiteFeatureFlags,
  readBootstrappedFeatureFlagsPayload,
  siteFeatureFlagKeys,
} from "@shared/hooks/use-site-feature-flags";
import { resolveEntitledLandingPath } from "@shared/hooks/use-site-feature-flags/useLandingPath";
import { localizedPath } from "@shared/lib/localized-path";
import {
  isFeatureOn,
  type SiteFeatureFlags,
  type SiteFeatureFlagsPayload,
} from "@shared/lib/site-features";
import {
  isMoneyPath,
  isRemovedSubscriptionPath,
  matchAIFeatureKey,
} from "@shared/lib/paywall";
import { localizedRootPath } from "./lib/guestMode/returnUrl";

/** Fails closed: no payments, no BYOK, no AI features. */
export const SAFE_PAYMENT_CONFIG: PaymentConfigPublic = {
  paymentsEnabled: false,
  byokEnabled: false,
  aiFeaturesEnabled: {},
  defaultCurrency: "USD",
  lowBalanceThresholdCents: 100,
};

export function localizedCurrentRoot(): string {
  return localizedRootPath(
    typeof window !== "undefined" ? window.location.pathname : "",
  );
}

export function stripGeneratedLanguagePrefix(pathname: string): string {
  if (pathname === "/:lang") return "/";
  // The `/` in the prefix guarantees the remainder is at least "/", so no
  // empty-string fallback is reachable here.
  if (pathname.startsWith("/:lang/")) return pathname.slice("/:lang".length);
  return pathname;
}

export async function readPaymentConfigForRoute(): Promise<PaymentConfigPublic> {
  try {
    const response = await fetch("/api/payment/config/public/", {
      cache: "no-store",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return SAFE_PAYMENT_CONFIG;
    const payload = (await response.json()) as Partial<PaymentConfigPublic>;
    // `aiFeaturesEnabled` means "this feature exists here". Either kill switch
    // being on is enough to keep the map — mirrors `usePaymentConfig` and
    // `build_public_payment_config()` on the server.
    const aiSurfacesExist = Boolean(
      payload.paymentsEnabled || payload.byokEnabled,
    );
    return {
      ...SAFE_PAYMENT_CONFIG,
      ...payload,
      byokEnabled: Boolean(payload.byokEnabled),
      aiFeaturesEnabled: aiSurfacesExist ? (payload.aiFeaturesEnabled ?? {}) : {},
    };
  } catch {
    return SAFE_PAYMENT_CONFIG;
  }
}

/**
 * Whether an AI route may mount at all.
 *
 * "May mount" is not "is usable". The anonymous config cannot know whether
 * *this* user holds a key, so the router only asks whether the feature exists
 * on this deployment; `AIFeatureProtectedRoute` resolves the per-user half and
 * renders the unlock CTA in place of the feature. Redirecting a keyless user
 * away from a page that is one link from working is hostile.
 */
export function isAIRouteAvailable(
  config: PaymentConfigPublic,
  featureKey: string,
): boolean {
  const featureExists = Boolean(config.aiFeaturesEnabled[featureKey]);
  return (config.paymentsEnabled || config.byokEnabled) && featureExists;
}

export async function enforcePaymentRouteAvailability(route: { path: string }) {
  const routePath = stripGeneratedLanguagePrefix(route.path);
  if (isRemovedSubscriptionPath(routePath)) {
    throw notFound();
  }
  const paidRoute = isMoneyPath(routePath);
  const aiFeatureKey = matchAIFeatureKey(routePath);

  if (!paidRoute && !aiFeatureKey) return;

  const config = await readPaymentConfigForRoute();

  // Money surfaces answer to `paymentsEnabled` and nothing else. BYOK never
  // opens one: a user on their own key is not buying anything, and a free
  // deployment has nothing to sell.
  if (paidRoute) {
    if (!config.paymentsEnabled) {
      throw redirect({ to: localizedCurrentRoot() as unknown as "/" });
    }
    return;
  }

  if (aiFeatureKey && isAIRouteAvailable(config, aiFeatureKey)) return;
  throw redirect({ to: localizedCurrentRoot() as unknown as "/" });
}

// ── Site feature flags ────────────────────────────────────────────────

/**
 * The flag map for a route guard, cheapest source first: the react-query
 * cache, then the SSR bootstrap (seeded into the cache), and only when
 * neither exists a network fetch. No fetch per navigation. `null` (nothing
 * reachable) makes every flagged route fail closed.
 */
export async function readFeatureFlagsForRoute(
  queryClient?: QueryClient,
): Promise<SiteFeatureFlags | null> {
  const cached = queryClient?.getQueryData<SiteFeatureFlagsPayload>(siteFeatureFlagKeys.all);
  if (cached) return cached.featureFlags;
  const bootstrap = readBootstrappedFeatureFlagsPayload();
  if (bootstrap) {
    queryClient?.setQueryData(siteFeatureFlagKeys.all, bootstrap);
    return bootstrap.featureFlags;
  }
  try {
    const payload = await fetchSiteFeatureFlags(queryClient);
    queryClient?.setQueryData(siteFeatureFlagKeys.all, payload);
    return payload.featureFlags;
  } catch {
    return null;
  }
}

/** Payment config without a request: query cache, then SSR bootstrap. */
export function readPaymentConfigSnapshot(
  queryClient?: QueryClient,
): PaymentConfigPublic | null {
  return (
    queryClient?.getQueryData<PaymentConfigPublic>(paywallKeys.all) ??
    readBootstrappedConfig() ??
    null
  );
}

/** Localized post-login landing page: first enabled feature, never a disabled one. */
export function landingPathForRoute(
  flags: SiteFeatureFlags | null,
  queryClient?: QueryClient,
): string {
  const landing = resolveEntitledLandingPath(flags, readPaymentConfigSnapshot(queryClient));
  return localizedPath(
    landing,
    typeof window !== "undefined" ? window.location.pathname : "",
  );
}

/**
 * Redirect a route whose site feature is off to the landing page. Reads the
 * generated `route.feature`; core routes (no `feature`) pass untouched. The
 * landing page is an enabled feature (or a core page), so this never loops.
 */
export async function enforceFeatureRouteAvailability(
  route: { path: string; feature?: string },
  queryClient?: QueryClient,
): Promise<void> {
  if (!route.feature) return;
  const flags = await readFeatureFlagsForRoute(queryClient);
  if (isFeatureOn(flags, route.feature)) return;
  // Redirects are rare; only then is it worth a request to learn whether the
  // AI landing page is entitled here (no SSR bootstrap in dev, cold cache).
  if (!readPaymentConfigSnapshot(queryClient)) {
    queryClient?.setQueryData(paywallKeys.all, await readPaymentConfigForRoute());
  }
  throw redirect({ to: landingPathForRoute(flags, queryClient) as unknown as "/" });
}
